import { useRef, useState, useEffect } from "react";

/**
 * SectionPattern — Decorative SVG henna-inspired pattern that reveals on scroll.
 * Options: 'mandala', 'border', 'corner', 'vine'
 */
const patterns = {
  mandala: {
    viewBox: "0 0 200 60",
    paths: [
      // Center diamond
      "M100 5 L105 30 L100 55 L95 30 Z",
      // Left petals
      "M95 30 Q70 10 60 30 Q70 50 95 30",
      // Right petals
      "M105 30 Q130 10 140 30 Q130 50 105 30",
      // Outer left
      "M60 30 Q40 15 30 30 Q40 45 60 30",
      // Outer right
      "M140 30 Q160 15 170 30 Q160 45 140 30",
      // Dots top
      "M100 5 M80 18 M120 18",
      // Small dots
      "M70 22 M130 22 M75 38 M125 38",
      // Tiny top dots
      "M88 10 M112 10",
    ],
  },
  border: {
    viewBox: "0 0 200 20",
    paths: [
      // Main line
      "M0 10 L200 10",
      // Left ornament
      "M20 10 Q15 5 25 5 Q35 5 30 10",
      // Right ornament
      "M170 10 Q165 5 175 5 Q185 5 180 10",
      // Center ornament
      "M95 10 Q90 3 100 3 Q110 3 105 10",
      // Diamond accents
      "M50 7 L52 10 L50 13 L48 10 Z",
      "M150 7 L152 10 L150 13 L148 10 Z",
    ],
  },
  corner: {
    viewBox: "0 0 200 80",
    paths: [
      // Top left corner
      "M10 10 L40 10 M10 10 L10 40",
      // Top left curve
      "M10 10 Q30 5 35 25",
      // Top right corner
      "M190 10 L160 10 M190 10 L190 40",
      // Top right curve
      "M190 10 Q170 5 165 25",
      // Bottom left corner
      "M10 70 L40 70 M10 70 L10 40",
      // Bottom left curve
      "M10 70 Q30 75 35 55",
      // Bottom right corner
      "M190 70 L160 70 M190 70 L190 40",
      // Bottom right curve
      "M190 70 Q170 75 165 55",
    ],
  },
  vine: {
    viewBox: "0 0 200 40",
    paths: [
      // Main vine
      "M0 20 Q50 5 100 20 Q150 35 200 20",
      // Leaf left
      "M40 12 Q30 5 35 18 Q42 14 40 12",
      // Leaf center
      "M90 15 Q80 8 85 22 Q92 18 90 15",
      // Leaf right
      "M140 25 Q130 32 135 20 Q142 23 140 25",
      // Small curl
      "M110 22 Q115 28 108 30 Q102 28 110 22",
      // Top dots
      "M65 10 M135 30",
    ],
  },
};

const SectionPattern = ({ variant = "mandala", className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // Check reduced motion
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setIsReducedMotion(mq.matches);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  const pattern = patterns[variant] || patterns.mandala;
  const strokeWidth = variant === "border" ? 1 : 1.5;

  if (isReducedMotion) return null;

  return (
    <div
      ref={ref}
      className={`relative w-full overflow-hidden ${className}`}
      style={{ height: variant === "border" ? "24px" : "70px" }}
      aria-hidden="true"
    >
      <svg
        viewBox={pattern.viewBox}
        className="w-full h-full opacity-30"
        preserveAspectRatio="xMidYMid meet"
      >
        {pattern.paths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke="url(#goldGrad)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: isVisible ? "none" : "1000",
              strokeDashoffset: isVisible ? "0" : "1000",
              transition: `stroke-dashoffset ${1.2 + i * 0.2}s cubic-bezier(0.16, 1, 0.3, 1)`,
            }}
          />
        ))}
        {/* Gold gradient definition */}
        <defs>
          <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#C9A882" stopOpacity="0" />
            <stop offset="30%" stopColor="#D4AF7A" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#E5C99A" stopOpacity="0.8" />
            <stop offset="70%" stopColor="#D4AF7A" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#C9A882" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default SectionPattern;
